import type { SyncSnapshot } from "./sync-client";

function pendingWords(pending: number): string {
  return pending === 1 ? "1 messaggio" : `${pending} messaggi`;
}

/**
 * Unico predicato per il registro bianco del badge e per la riga esplicativa:
 * tenerli separati li farebbe divergere.
 */
export function syncNeedsAttention(sync: SyncSnapshot): boolean {
  if (sync.pending === 0) return false;
  return sync.state === "offline" || sync.state === "unreachable" || sync.state === "error";
}

export function syncBadgeLabel(sync: SyncSnapshot): string {
  switch (sync.state) {
    case "offline":
      return "Offline";
    case "unreachable":
      return "Non connesso";
    case "connecting":
      return "Riprovo…";
    case "unavailable":
      return "Sync non attiva";
    case "syncing":
      if (sync.activity === "waiting") return `In attesa · ${sync.pending}`;
      if (sync.activity === "confirmation") return `Conferma · ${sync.pending}`;
      return sync.pending > 0 ? `Invio ${sync.pending}` : "Invio";
    case "error":
      return "Errore sync";
    default:
      return "Sincronizzato";
  }
}

export interface SyncErrorDetail {
  cause: string;
  queue: string;
  lastContact: string;
}

/**
 * Deliberatamente separata da `syncExplanation`: quella spiega il lavoro fermo
 * ed e' legata alla coda, questa spiega la causa e deve reggere anche a coda
 * vuota. Riusarne una per l'altra le farebbe divergere al primo ritocco.
 */
export function syncErrorDetail(sync: SyncSnapshot): SyncErrorDetail {
  return {
    cause: sync.message ?? "Causa non riferita dal relay.",
    queue: `Messaggi da inviare: ${sync.pending}.`,
    lastContact: sync.lastContact
      ? `Ultimo contatto: ${formatContact(sync.lastContact)}.`
      : "Nessun contatto in questa sessione.",
  };
}

/** Un orario locale leggibile; la data solo quando non e' oggi. */
function formatContact(instant: string, now = new Date()): string {
  const contact = new Date(instant);
  if (Number.isNaN(contact.getTime())) return instant;
  const time = contact.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (contact.toDateString() === now.toDateString()) return time;
  return `${contact.toLocaleDateString("it-IT", { day: "numeric", month: "long" })}, ${time}`;
}

export function syncExplanation(sync: SyncSnapshot): string | undefined {
  /**
   * L'unico stato che va spiegato anche a coda vuota: non si risolve da solo e
   * non dipende dalla rete. Un canale che rifiuta il lasciapassare scaduto
   * lascerebbe altrimenti un "Sync non attiva" senza causa.
   */
  if (sync.state === "unavailable") return sync.message;
  if (sync.pending === 0) return undefined;
  const queue = `Dati salvati sul dispositivo. ${pendingWords(sync.pending)} da sincronizzare.`;
  switch (sync.state) {
    case "offline":
      return `Offline. ${queue}`;
    case "unreachable":
      return `Relay non raggiungibile. ${queue}`;
    case "connecting":
      return `Contatto il relay… ${queue}`;
    case "syncing":
      if (sync.activity === "waiting") {
        const reason =
          sync.waitReason === "traffic"
            ? "Pausa temporanea degli invii."
            : sync.waitReason === "recipients"
              ? "Attendo la conferma degli accessi."
              : "Invio in attesa.";
        const retry = sync.retryAt
          ? `Ripresa automatica alle ${new Date(sync.retryAt).toLocaleTimeString("it-IT")}.`
          : "La sincronizzazione riprende automaticamente.";
        return `${queue} ${reason} ${retry}`;
      }
      if (sync.activity === "confirmation")
        return `${queue} Attendo la conferma del server; se manca, riprovo automaticamente.`;
      return `${queue} Invio al server in corso.`;
    case "error":
      return `${sync.message ?? "Errore di sincronizzazione."} ${queue}${sync.activity === "waiting" ? " Riprovo automaticamente." : ""}`;
    default:
      return undefined;
  }
}
